import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { useNavigate } from 'react-router-dom';
import ProviderMark from './provider-mark';
import useMCPConnections from './use-mcp-connections';

const providers = [
  { id: 'chatgpt', name: 'ChatGPT', descriptionKey: 'chatgpt_card_description', description: 'Manage your social content from ChatGPT and Codex.' },
  { id: 'claude', name: 'Claude', descriptionKey: 'claude_card_description', description: 'Manage your social content from Claude and Claude Desktop.' },
];

const belongsToProvider = (connection, provider) => (connection?.name || '').toLowerCase().includes(provider);

export default function AIConnections() {
  const navigate = useNavigate();
  const { data, error, loading, tr } = useMCPConnections();

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', px: { xs: 1, md: 3 }, pt: { xs: 1, md: 2 }, pb: 10 }}>
      <Stack spacing={0.75} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>{tr('integrations_title', 'AI integrations')}</Typography>
        <Typography color="text.secondary">{tr('integrations_subtitle', 'Connect Brand2Social with AI assistants to review and manage your social content.')}</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ minHeight: 280, display: 'grid', placeItems: 'center' }}><CircularProgress aria-label={tr('loading_integrations', 'Loading integrations')} /></Box> : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            {providers.map((provider) => {
              const connection = (data?.connections || []).find((item) => belongsToProvider(item, provider.id));
              return (
                <Card key={provider.id} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', transition: 'border-color .2s, box-shadow .2s, transform .2s', '&:hover': { borderColor: 'primary.main', boxShadow: '0 12px 32px rgba(27, 77, 62, .08)', transform: 'translateY(-2px)' } }}>
                  <CardActionArea onClick={() => navigate(`/ai-connections/${provider.id}`)} sx={{ height: '100%', p: 0 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <ProviderMark provider={provider.id} size={52} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="h6" fontWeight={700}>{provider.name}</Typography>
                            <Chip size="small" color={connection ? 'success' : 'default'} variant={connection ? 'filled' : 'outlined'} label={connection ? tr('connected', 'Connected') : tr('not_connected', 'Not connected')} />
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.7 }}>{tr(provider.descriptionKey, provider.description)}</Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 2, color: 'primary.main' }}>
                            <Typography variant="body2" fontWeight={700}>{connection ? tr('manage', 'Manage connection') : tr('view_setup', 'View setup')}</Typography>
                            <ArrowForwardRoundedIcon fontSize="small" />
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>

          <Card variant="outlined" sx={{ mt: 3, borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <ProviderMark provider="mcp" size={44} />
                <Box>
                  <Typography variant="h6" fontWeight={700}>{tr('mcp_standard_title', 'Powered by MCP')}</Typography>
                  <Typography variant="body2" color="text.secondary">{tr('mcp_standard_description', 'Both integrations use the same secure Brand2Social MCP server and OAuth authorization flow.')}</Typography>
                </Box>
              </Stack>
              <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                {[
                  [tr('read_title', 'Read & review'), tr('gallery_read', 'Brands, profiles, drafts, posts, comments and reviews.')],
                  [tr('write_title', 'Create & publish'), tr('gallery_write', 'Upload media, prepare drafts and schedule posts.')],
                  [tr('permission_control', 'Permission controlled'), tr('gallery_permission', 'Your organization roles and approval rules still apply.')],
                ].map(([title, description]) => <Box key={title} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}><Typography variant="subtitle2" fontWeight={700}>{title}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.6 }}>{description}</Typography></Box>)}
              </Box>
            </CardContent>
          </Card>

          <Box sx={{ mt: 4 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h6" fontWeight={700}>{tr('connections', 'Active integrations')}</Typography>
              <Chip size="small" label={(data?.connections || []).length} />
            </Stack>
            {!data?.connections?.length ? (
              <Box sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper' }}>
                <HubOutlinedIcon sx={{ color: 'text.disabled', fontSize: 38, mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>{tr('empty', 'No AI integrations connected yet.')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{tr('empty_help', 'Choose ChatGPT or Claude above to view connection instructions.')}</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {(data?.connections || []).map((connection) => {
                  const provider = (connection.name || '').toLowerCase().includes('claude') ? 'claude' : 'chatgpt';
                  const organization = data.organizations?.find((org) => org.id === connection.org_id)?.name || tr('unavailable_org', 'Organization no longer accessible');
                  return <Card key={connection.id} variant="outlined" sx={{ borderRadius: 2.5 }}><CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}><ProviderMark provider={provider} size={42} /><Box sx={{ flex: 1 }}><Typography fontWeight={700}>{connection.name}</Typography><Typography variant="body2" color="text.secondary">{organization} · {(connection.scope || '').split(' ').includes('brand2social:write') ? tr('write_access', 'Read and write access') : tr('read_only', 'Read only')}</Typography></Box><Button variant="outlined" onClick={() => navigate(`/ai-connections/${provider}`)}>{tr('manage', 'Manage')}</Button></Stack></CardContent></Card>;
                })}
              </Stack>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
